import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  Block as CancelIcon,
  Close as CloseIcon,
  FileDownload as DownloadIcon,
  Print as PrintIcon,
  Undo as RefundIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, startOfMonth } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import CancelSaleDialog from "../../components/sales/CancelSaleDialog";
import { rowsToCsv } from "../../utils/csvExport";
import RefundSaleDialog from "../../components/sales/RefundSaleDialog";
import SaleBillDrawer from "../../components/sales/SaleBillDrawer";
import reportAPI, { SalesReportFilter, SalesReportRow, SalesReportSortField } from "../../services/reportAPI";
import financialYearAPI, {
  FinancialYear,
  formatFinancialYearDisplay,
  indiaFyMeta,
} from "../../services/financialYearAPI";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { userAPI, User } from "../../services/userAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import { useTradeTerminology } from "../../utils/tradeTerminology";
import { billSeqDigits } from "../../utils/billReceipt.helper";
import {
  paymentMethodChipSx,
  paymentMethodLabel,
} from "../../utils/paymentMethodUi";

const formatCurrency = (value: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

/** Format YYYY-MM-DD as calendar label (no UTC midnight shift). */
const formatYmdLabel = (ymd: string) => {
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return format(new Date(y, m - 1, d), "dd MMM yyyy");
};

/** Always show sale timestamps in Asia/Kolkata. */
const formatIstDateTime = (iso: string) =>
  new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(iso));

/** Default range = current calendar month (1st → today), not FY 1 Apr. */
const defaultStart = () => format(startOfMonth(new Date()), "yyyy-MM-dd");
const defaultEnd = () => format(new Date(), "yyyy-MM-dd");

function downloadCsv(filename: string, rows: Array<Record<string, unknown>>) {
  const blob = new Blob([rowsToCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const SalesReportPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { canDelete } = useRBAC();
  const { terms } = useTradeTerminology();
  const { user } = useAuthStore();
  const role = String(user?.role || "").toUpperCase();
  const isSuperAdmin = role === "SUPER_ADMIN" || role === "SUPERADMIN";
  const isAdmin = role === "ADMIN";
  const canDeleteSale = canDelete("sales");
  const [actionMsg, setActionMsg] = useState<{ type: "success" | "error"; text: string } | null>(
    null
  );
  const [cancelTarget, setCancelTarget] = useState<{
    id: string;
    saleNumber?: string;
    netAmount: number;
    status?: string;
  } | null>(null);
  const [refundTarget, setRefundTarget] = useState<{
    id: string;
    saleNumber?: string;
    netAmount: number;
    status?: string;
  } | null>(null);
  const [viewSale, setViewSale] = useState<SalesReportRow | null>(null);
  const [activeTab, setActiveTab] = useState<"sales" | "returns">("sales");
  const [returnPage, setReturnPage] = useState(0);
  const [returnRowsPerPage, setReturnRowsPerPage] = useState(25);
  const [returnSearch, setReturnSearch] = useState("");
  const [viewCreditNote, setViewCreditNote] = useState<any | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [draft, setDraft] = useState<SalesReportFilter>({
    startDate: defaultStart(),
    endDate: defaultEnd(),
    branchId: isAdmin ? user?.branchId : undefined,
    salesPointId: undefined,
    userId: undefined,
    fyCode: indiaFyMeta().code,
  });
  const [filters, setFilters] = useState<SalesReportFilter>(draft);
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<SalesReportSortField>("createdAt", "desc");

  const { data: returnsData, isLoading: returnsLoading } = useQuery({
    queryKey: [
      "salesReturnsReport",
      filters.startDate,
      filters.endDate,
      filters.branchId,
      filters.salesPointId,
      returnSearch,
      returnPage,
      returnRowsPerPage,
    ],
    queryFn: () =>
      reportAPI.getSalesReturns({
        startDate: filters.startDate,
        endDate: filters.endDate,
        branchId: filters.branchId,
        salesPointId: filters.salesPointId,
        search: returnSearch.trim() || undefined,
        pageNo: returnPage + 1,
        pageSize: returnRowsPerPage,
      }),
    enabled: activeTab === "returns",
  });

  const { data: financialYears = [] } = useQuery({
    queryKey: ["financialYears"],
    queryFn: () => financialYearAPI.list(),
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["branchesAll"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["salesPointsAll"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
  });

  const { data: salesPointUsers = [], isFetching: loadingSoldBy } = useQuery({
    queryKey: ["salesReportUsers", draft.salesPointId, draft.branchId],
    queryFn: async (): Promise<User[]> => {
      const spId = draft.salesPointId;
      if (!spId) return [];
      const res = await userAPI.getUsers({
        salesPointId: spId,
        branchId: draft.branchId || (isAdmin ? user?.branchId : undefined) || undefined,
        pageNo: 1,
        pageSize: 200,
        isActive: true,
      });
      // Defense: only users assigned to this sales point
      return (res.users || []).filter((u) => u.salesPointId === spId);
    },
    enabled: Boolean(draft.salesPointId),
  });

  const branchSalesPoints = useMemo(() => {
    const bid = isAdmin ? user?.branchId : draft.branchId;
    if (!bid) return salesPoints;
    return salesPoints.filter((d) => d.branchId === bid);
  }, [salesPoints, draft.branchId, isAdmin, user?.branchId]);

  useEffect(() => {
    if (isAdmin && user?.branchId) {
      setDraft((p) => ({ ...p, branchId: user.branchId }));
    }
  }, [isAdmin, user?.branchId]);

  useEffect(() => {
    // Drop cashier filter when sales point cleared or user left that point
    if (!draft.salesPointId) {
      setDraft((p) => (p.userId ? { ...p, userId: undefined } : p));
      return;
    }
    if (
      draft.userId &&
      salesPointUsers.length > 0 &&
      !salesPointUsers.some((u) => u.id === draft.userId)
    ) {
      setDraft((p) => ({ ...p, userId: undefined }));
    }
  }, [draft.salesPointId, draft.userId, salesPointUsers]);

  const queryKey = useMemo(
    () => ["sales-report", filters, page, rowsPerPage, sortBy, sortOrder],
    [filters, page, rowsPerPage, sortBy, sortOrder]
  );

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      reportAPI.getSalesReport({
        ...filters,
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...sortParams,
      }),
    enabled: Boolean(filters.startDate && filters.endDate),
    staleTime: 0,
  });

  const applyFilters = () => {
    setPage(0);
    setFilters({
      ...draft,
      branchId: isAdmin ? user?.branchId : draft.branchId || undefined,
    });
  };

  const onSort = (field: SalesReportSortField) =>
    handleSort(field, () => setPage(0));

  const [exporting, setExporting] = useState(false);
  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const res = await reportAPI.getSalesReport({
        ...filters,
        pageNo: 1,
        pageSize: 10000,
        ...sortParams,
      });
      const exportRows = (res.sales || []).map((r) => ({
        "Date": formatIstDateTime(r.createdAt),
        "Bill No": r.saleNumber,
        "Order Type": r.orderType,
        "Sales Point": r.salesPoint?.name || "",
        "Branch": r.branch?.name || "",
        "Total (₹)": r.totalAmount,
        "Discount (₹)": r.discountAmount,
        "Tax (₹)": r.taxAmount,
        "Net Amount (₹)": r.netAmount,
        "Payment Method": r.paymentMethod,
        "Status": r.status,
        "Sold By": r.creator?.name || "",
      }));
      downloadCsv(
        `sales_report_${filters.startDate}_to_${filters.endDate}.csv`,
        exportRows
      );
    } catch (e: any) {
      setActionMsg({
        type: "error",
        text: e?.message || "Failed to export CSV",
      });
    } finally {
      setExporting(false);
    }
  };

  const errorMsg =
    error instanceof Error
      ? error.message
      : (error as any)?.response?.data?.message || "Failed to load sales report";

  return (
    <Box>
      {actionMsg && (
        <Alert
          severity={actionMsg.type}
          sx={{ mb: 2 }}
          onClose={() => setActionMsg(null)}
        >
          {actionMsg.text}
        </Alert>
      )}
      <PageHeader
        title={terms.salesReportLabel}
        subtitle={
          isSuperAdmin
            ? "Branch and salesPoint wise sales (all branches)"
            : "Sales for your branch only"
        }
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: terms.salesReportLabel },
        ]}
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <Button
          variant={activeTab === "sales" ? "contained" : "outlined"}
          color="primary"
          onClick={() => setActiveTab("sales")}
          startIcon={<AssessmentIcon />}
          sx={{ fontWeight: 700 }}
        >
          {terms.salesInvoicesTabLabel || "Sales Invoices"}
        </Button>
        <Button
          variant={activeTab === "returns" ? "contained" : "outlined"}
          color="warning"
          onClick={() => setActiveTab("returns")}
          startIcon={<RefundIcon />}
          sx={{ fontWeight: 700 }}
        >
          {terms.returnsTabLabel || "Returns & Credit Notes"}
        </Button>
      </Stack>

      <Paper sx={{ p: 1.5, mb: 1.5, border: "1px solid", borderColor: "divider" }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={4} md={1.5} sx={{ maxWidth: { sm: 180, md: 160 } }}>
            <FormControl fullWidth size="small">
              <InputLabel>Financial year</InputLabel>
              <Select
                label="Financial year"
                value={draft.fyCode || ""}
                onChange={(e) => {
                  const code = e.target.value || undefined;
                  // Keep date pickers on current month; do not jump to FY 1 Apr.
                  setDraft((p) => ({
                    ...p,
                    fyCode: code,
                  }));
                }}
              >
                {(financialYears as FinancialYear[]).length > 0
                  ? (financialYears as FinancialYear[]).map((fy) => (
                      <MenuItem key={fy.id} value={fy.code}>
                        {formatFinancialYearDisplay(fy)}
                        {fy.isCurrent ? " (current)" : ""}
                      </MenuItem>
                    ))
                  : (
                      <MenuItem value={indiaFyMeta().code}>
                        {indiaFyMeta().display} (current)
                      </MenuItem>
                    )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={1.5} sx={{ maxWidth: { sm: 160, md: 150 } }}>
            <TextField
              fullWidth
              size="small"
              label="From"
              type="date"
              value={draft.startDate}
              onChange={(e) => setDraft((p) => ({ ...p, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={1.5} sx={{ maxWidth: { sm: 160, md: 150 } }}>
            <TextField
              fullWidth
              size="small"
              label="To"
              type="date"
              value={draft.endDate}
              onChange={(e) => setDraft((p) => ({ ...p, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {isSuperAdmin && (
            <Grid item xs={12} sm={4} md={2} sx={{ maxWidth: { md: 180 } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  value={draft.branchId || ""}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      branchId: e.target.value || undefined,
                      salesPointId: undefined,
                      userId: undefined,
                    }))
                  }
                >
                  <MenuItem value="">All branches</MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {isAdmin && user?.branchId && (
            <Grid item xs={12} sm={4} md={2} sx={{ maxWidth: { md: 180 } }}>
              <TextField
                fullWidth
                size="small"
                label="Branch"
                value={
                  branches.find((b) => b.id === user.branchId)?.name ||
                  "Your branch"
                }
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12} sm={4} md={2} sx={{ maxWidth: { md: 180 } }}>
            <FormControl fullWidth size="small">
              <InputLabel>Sales point</InputLabel>
              <Select
                label="Sales point"
                value={draft.salesPointId || ""}
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    salesPointId: e.target.value || undefined,
                    userId: undefined,
                  }))
                }
              >
                <MenuItem value="">All sales points</MenuItem>
                {branchSalesPoints.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {draft.salesPointId ? (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sold by</InputLabel>
                <Select
                  label="Sold by"
                  value={draft.userId || ""}
                  disabled={loadingSoldBy}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      userId: e.target.value || undefined,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {salesPointUsers.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                      {u.role ? ` · ${u.role}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={6} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AssessmentIcon />}
                onClick={applyFilters}
                disabled={isFetching}
              >
                Generate
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCsv}
                disabled={exporting || isFetching || !(data?.sales && data.sales.length > 0)}
              >
                CSV
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {activeTab === "sales" && isLoading && (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton height={80} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton height={300} />
          </Grid>
        </Grid>
      )}

      {activeTab === "sales" && !isLoading && data && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip
              size="small"
              label={`${formatYmdLabel(filters.startDate)} – ${formatYmdLabel(filters.endDate)}`}
            />
            {data.branch && (
              <Chip size="small" color="primary" label={`Branch: ${data.branch.name}`} />
            )}
            {data.salesPoint && (
              <Chip size="small" color="secondary" label={`Dept: ${data.salesPoint.name}`} />
            )}
            {filters.userId && (
              <Chip
                size="small"
                variant="outlined"
                label={`Sold by: ${
                  salesPointUsers.find((u) => u.id === filters.userId)?.name ||
                  "Selected user"
                }`}
              />
            )}
            {!data.branch && isSuperAdmin && (
              <Chip size="small" variant="outlined" label="All branches" />
            )}
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(6, minmax(0, 1fr))",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {(
              [
                {
                  label: "Total sales",
                  value: String(data.summary.totalSales),
                  accent: "#0284c7",
                  bg: "linear-gradient(160deg, #f0f9ff 0%, #e0f2fe 55%, #bae6fd 100%)",
                },
                {
                  label: "Gross amount",
                  value: formatCurrency(data.summary.totalAmount),
                  accent: "#0d9488",
                  bg: "linear-gradient(160deg, #f0fdfa 0%, #ccfbf1 55%, #99f6e4 100%)",
                },
                {
                  label: "Net amount",
                  value: formatCurrency(data.summary.totalNet),
                  accent: "#16a34a",
                  bg: "linear-gradient(160deg, #f0fdf4 0%, #dcfce7 55%, #bbf7d0 100%)",
                },
                {
                  label: "Tax",
                  value: formatCurrency(data.summary.totalTax),
                  accent: "#d97706",
                  bg: "linear-gradient(160deg, #fffbeb 0%, #fef3c7 55%, #fde68a 100%)",
                },
                {
                  label: "Returns / Refunds",
                  value: String(data.refundedSummary?.totalRefunded || (returnsData?.totalRecords || 0)),
                  meta: formatCurrency(data.refundedSummary?.refundedNet || returnsData?.options?.extra?.totalRefundAmount || 0),
                  accent: "#ea580c",
                  bg: "linear-gradient(160deg, #fff7ed 0%, #ffedd5 55%, #fed7aa 100%)",
                },
                {
                  label: "Cancelled sales",
                  value: String(data.cancelledSummary?.totalCancelled || 0),
                  meta: formatCurrency(data.cancelledSummary?.cancelledNet || 0),
                  accent: "#e11d48",
                  bg: "linear-gradient(160deg, #fff1f2 0%, #ffe4e6 55%, #fecdd3 100%)",
                },
              ] as Array<{
                label: string;
                value: string;
                meta?: string;
                accent: string;
                bg: string;
              }>
            ).map((tile) => (
              <Card
                key={tile.label}
                elevation={0}
                sx={{
                  height: "100%",
                  border: "1px solid",
                  borderColor: "transparent",
                  borderRadius: 2.5,
                  background: tile.bg,
                  boxShadow: `inset 4px 0 0 ${tile.accent}`,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: tile.accent, letterSpacing: 0.2 }}
                  >
                    {tile.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ mt: 0.5, color: "#0f172a", lineHeight: 1.2 }}
                  >
                    {tile.value}
                  </Typography>
                  {tile.meta ? (
                    <Typography variant="body2" sx={{ mt: 0.5, color: "#64748b" }}>
                      {tile.meta}
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {isSuperAdmin && !filters.branchId && data.byBranch.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Branch wise
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Branch</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell align="right">Net</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.byBranch.map((row) => (
                          <TableRow key={row.branchId}>
                            <TableCell>{row.branchName}</TableCell>
                            <TableCell align="right">{row.saleCount}</TableCell>
                            <TableCell align="right">{formatCurrency(row.totalNet)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} md={isSuperAdmin && !filters.branchId ? 6 : 12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    SalesPoint wise
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SalesPoint</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Net</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.bySalesPoint.map((row) => (
                        <TableRow key={row.salesPointId}>
                          <TableCell>{row.salesPointName}</TableCell>
                          <TableCell>{row.branchName}</TableCell>
                          <TableCell align="right">{row.saleCount}</TableCell>
                          <TableCell align="right">{formatCurrency(row.totalNet)}</TableCell>
                        </TableRow>
                      ))}
                      {data.bySalesPoint.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No sales in range
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Sale transactions
              </Typography>
              <TableContainer>
                <Table size="small" sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <SortableTableCell label="Date" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Sale #" field="saleNumber" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Net" field="netAmount" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                      <SortableTableCell label="Sales point" field="salesPoint" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Payment" field="paymentMethod" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Status" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Holder" field="holderName" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <SortableTableCell label="Sold by" field="soldBy" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                      <TableCell align="right">Bill</TableCell>
                      {canDeleteSale && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.sales.map((sale) => (
                      <TableRow key={sale.id} hover>
                        <TableCell>
                          {formatIstDateTime(sale.createdAt)}
                        </TableCell>
                        <TableCell>{billSeqDigits(sale.saleNumber)}</TableCell>
                        <TableCell align="right">{formatCurrency(sale.netAmount)}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 96,
                            width: 96,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={sale.salesPoint?.name || undefined}
                        >
                          {sale.salesPoint?.name || "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={paymentMethodLabel(sale.paymentMethod)}
                            sx={paymentMethodChipSx(sale.paymentMethod)}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip
                              size="small"
                              label={sale.status}
                              color={
                                sale.status === "cancelled" || sale.status === "refunded"
                                  ? "error"
                                  : sale.status === "completed"
                                    ? "success"
                                    : "default"
                              }
                              variant={sale.status === "cancelled" || sale.status === "refunded" ? "filled" : "outlined"}
                            />
                            {((sale.creditNotes && sale.creditNotes.length > 0) || (sale.notes && sale.notes.includes("Partial Return"))) && (
                              <Tooltip
                                title={
                                  sale.creditNotes && sale.creditNotes.length > 0
                                    ? sale.creditNotes.map((c) => `#${c.creditNoteNumber} (₹${c.netAmount})`).join(", ")
                                    : sale.notes || "Items returned"
                                }
                              >
                                <Chip
                                  size="small"
                                  color="warning"
                                  label="Partially Returned"
                                  variant="filled"
                                  sx={{ fontWeight: 700, fontSize: "0.68rem", height: 22 }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{sale.cardHolder?.holderName || "—"}</TableCell>
                        <TableCell>{sale.creator?.name || "—"}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="View bill">
                            <IconButton
                              size="small"
                              onClick={() => setViewSale(sale)}
                              aria-label={`View bill ${sale.saleNumber}`}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {sale.status === "completed" && (
                              <Tooltip title="Return / Refund sale">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() =>
                                    setRefundTarget({
                                      id: sale.id,
                                      saleNumber: sale.saleNumber,
                                      netAmount: sale.netAmount,
                                      status: sale.status,
                                    })
                                  }
                                  aria-label={`Refund sale ${sale.saleNumber}`}
                                >
                                  <RefundIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDeleteSale &&
                              sale.status !== "cancelled" &&
                              sale.status !== "refunded" && (
                                <Tooltip title="Cancel sale">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        setCancelTarget({
                                          id: sale.id,
                                          saleNumber: sale.saleNumber,
                                          netAmount: sale.netAmount,
                                          status: sale.status,
                                        })
                                      }
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.sales.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={canDeleteSale ? 10 : 9} align="center" sx={{ py: 4 }}>
                          No completed sales in this period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                count={data.pagination.totalRecords}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* ================= RETURNS & CREDIT NOTES TAB VIEW ================= */}
      {activeTab === "returns" && (
        <Box sx={{ mt: 1 }}>
          {/* Summary Metric Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            <Paper sx={{ p: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Total Returns Count
              </Typography>
              <Typography variant="h4" fontWeight={800} color="warning.dark">
                {returnsData?.totalRecords || 0}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Total Refunded / Credit Amount
              </Typography>
              <Typography variant="h4" fontWeight={800} color="error.dark">
                {formatCurrency(returnsData?.options?.extra?.totalRefundAmount || 0)}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Inventory Restock Status
              </Typography>
              <Typography variant="h5" fontWeight={800} color="success.dark" sx={{ mt: 0.5 }}>
                ✅ Stock Restored
              </Typography>
            </Paper>
          </Box>

          {/* Search bar within returns */}
          <Box sx={{ mb: 2, display: "flex", gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search Credit Note #, Bill #, Reason..."
              value={returnSearch}
              onChange={(e) => {
                setReturnSearch(e.target.value);
                setReturnPage(0);
              }}
              sx={{ maxWidth: 360 }}
            />
            {returnSearch && (
              <Button size="small" onClick={() => setReturnSearch("")}>
                Clear
              </Button>
            )}
          </Box>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {terms.returnsRegisterLabel || "Returns & Credit Notes Register"}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Credit Note #</TableCell>
                      <TableCell>Original Bill #</TableCell>
                      <TableCell>{terms.customerSingular || "Customer"}</TableCell>
                      <TableCell>Returned {terms.itemPlural || "Items"}</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell align="right">Refund Amount</TableCell>
                      <TableCell align="right">Slip</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnsLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          Loading returns register…
                        </TableCell>
                      </TableRow>
                    ) : (returnsData?.items || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          No returns recorded in this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      returnsData.items.map((row: any) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{formatIstDateTime(row.createdAt)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color="warning"
                              label={row.creditNoteNumber}
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell>
                            <strong>#{row.sale?.saleNumber || "—"}</strong>
                          </TableCell>
                          <TableCell>
                            {row.sale?.customerName ||
                              (terms.vertical === "PHARMACY"
                                ? "Walk-in Patient"
                                : terms.vertical === "RESTAURANT"
                                ? "Dine-in / Walk-in Guest"
                                : "Walk-in Customer")}
                            {row.sale?.customerPhone && ` (${row.sale.customerPhone})`}
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              {(row.items || []).map((it: any, idx: number) => (
                                <Typography key={idx} variant="caption" display="block">
                                  • {it.product?.name || terms.itemSingular || "Item"} (Qty: {it.quantity})
                                </Typography>
                              ))}
                              {(!row.items || row.items.length === 0) && "—"}
                            </Stack>
                          </TableCell>
                          <TableCell>{row.reason || "Return / Refund"}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: "error.dark" }}>
                            {formatCurrency(row.netAmount)}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View / Print Return Slip">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => setViewCreditNote(row)}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                count={returnsData?.totalRecords || 0}
                page={returnPage}
                onPageChange={(_, p) => setReturnPage(p)}
                rowsPerPage={returnRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setReturnRowsPerPage(parseInt(e.target.value, 10));
                  setReturnPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Return Slip Print Modal */}
      <Dialog
        open={!!viewCreditNote}
        onClose={() => setViewCreditNote(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "warning.50", borderBottom: "1px solid", borderColor: "warning.200" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              {terms.returnSlipTitle || "Credit Note / Return Slip"}
            </Typography>
            <IconButton onClick={() => setViewCreditNote(null)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {viewCreditNote && (
            <Box>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
                <Typography variant="subtitle1" fontWeight={800} color="warning.dark">
                  Credit Note #{viewCreditNote.creditNoteNumber}
                </Typography>
                <Typography variant="body2">
                  Original Invoice: <strong>#{viewCreditNote.sale?.saleNumber}</strong>
                </Typography>
                <Typography variant="body2">
                  {terms.customerSingular || "Customer"}:{" "}
                  <strong>
                    {viewCreditNote.sale?.customerName ||
                      (terms.vertical === "PHARMACY"
                        ? "Walk-in Patient"
                        : terms.vertical === "RESTAURANT"
                        ? "Dine-in / Walk-in Guest"
                        : "Walk-in Customer")}
                  </strong>
                </Typography>
                <Typography variant="body2">
                  Date: {formatIstDateTime(viewCreditNote.createdAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Reason: {viewCreditNote.reason || "Customer return"}
                </Typography>
              </Paper>

              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Returned {terms.itemPlural || "Items"} (Stock Restored):
              </Typography>
              <Table size="small">
                <TableHead sx={{ bgcolor: "grey.100" }}>
                  <TableRow>
                    <TableCell>{terms.itemSingular || "Item"}</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Refund</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(viewCreditNote.items || []).map((it: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{it.product?.name || terms.itemSingular || "Item"}</TableCell>
                      <TableCell align="right"><strong>{it.quantity}</strong></TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(it.taxableAmount || 0) + Number(it.taxAmount || 0))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>
                  Total Refund Amount:
                </Typography>
                <Typography variant="h5" fontWeight={900} color="warning.dark">
                  {formatCurrency(viewCreditNote.netAmount)}
                </Typography>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => window.print()} variant="contained" color="warning" startIcon={<PrintIcon />}>
            Print Slip
          </Button>
          <Button onClick={() => setViewCreditNote(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <SaleBillDrawer
        open={!!viewSale}
        saleRow={viewSale}
        onClose={() => setViewSale(null)}
      />

      <CancelSaleDialog
        open={!!cancelTarget}
        sale={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onSuccess={() => {
          setCancelTarget(null);
          setActionMsg({ type: "success", text: "Sale cancelled" });
          queryClient.invalidateQueries({ queryKey: ["sales-report"] });
          refetch();
        }}
      />

      <RefundSaleDialog
        open={!!refundTarget}
        sale={refundTarget}
        onClose={() => setRefundTarget(null)}
        onSuccess={() => {
          setRefundTarget(null);
          setActionMsg({
            type: "success",
            text: "Sale returned / refunded successfully. Credit note generated and stock restored.",
          });
          queryClient.invalidateQueries({ queryKey: ["sales-report"] });
          queryClient.invalidateQueries({ queryKey: ["gst-refund"] });
          refetch();
        }}
      />
    </Box>
  );
};

export default SalesReportPage;
